/**
 * c-cart-drawer.js
 *
 * Powers the custom cart drawer:
 *  - "You May Also Like" slider at the foot of the cart (scroll-snap track +
 *    prev/next arrows; no slider library, so it survives a re-render)
 *  - Real-time add to cart from the YMAL slider and "Complete Your Routine"
 *    (adds via /cart/add.js, re-renders the drawer via the Section Rendering
 *    API, updates count badge + subtotal)
 *  - Quantity stepper / typed quantity / remove line via /cart/change.js
 *  - Upgrade prompt (snippets/c-cart-prompt.liquid): Duo upgrade swaps
 *    one unit of a line for its Duo; Subscribe moves a line onto the chosen
 *    selling plan
 *  - Dispatches the theme's standard `shopify:cart:lines-update` event so the
 *    rest of the theme (header bubble, product forms, quick-add) can react,
 *    and listens for the same event from elsewhere (PDP add-to-cart,
 *    quick-add, quick order list) to refresh itself.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

(() => {
  if (customElements.get('c-cart-drawer')) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SOURCE = 'c-cart-drawer';

  class CCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || 'cart-drawer-section';
      this.busy = false;
      this.pendingExternal = 0;

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));

      // The theme dispatches this event *before* its cart request completes and
      // attaches a promise that resolves once the cart is actually updated, so
      // wait for the promise before re-rendering. The drawer shows its busy
      // state meanwhile, as it does for its own quantity and YMAL updates.
      this.onExternalCartUpdate = async (event) => {
        // Our own broadcasts come after the drawer has already re-rendered.
        if (event.target instanceof Node && this.contains(event.target)) return;

        const settled = event.promise || Promise.resolve({ detail: event.detail });
        this.pendingExternal += 1;
        this.syncBusy();

        try {
          const result = await settled;
          const detail = (result && result.detail) || {};
          if (detail.source === SOURCE) return;

          const html = detail.sections && detail.sections[this.sectionId];
          if (html && this.renderFromHtml(html)) return;

          await this.renderFromServer();
        } catch (error) {
          await this.renderFromServer();
        } finally {
          this.pendingExternal -= 1;
          this.syncBusy();
        }
      };
      document.addEventListener(StandardEvents.cartLinesUpdate, this.onExternalCartUpdate);

      this.bindUpsellSlider();
    }

    disconnectedCallback() {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.onExternalCartUpdate);
      clearTimeout(this.upsellSyncTimer);
      clearTimeout(this.errorTimeout);
    }

    /* ------------------------------------------------------------------ */
    /* Events                                                             */
    /* ------------------------------------------------------------------ */

    handleClick(event) {
      const nav = event.target.closest('[data-ymal-nav]');
      if (nav) {
        this.scrollUpsells(nav.dataset.ymalNav === 'next' ? 1 : -1);
        return;
      }

      const addButton = event.target.closest('[data-add-variant]');
      if (addButton) {
        this.addToCart(addButton.dataset.addVariant, addButton);
        return;
      }

      const upgradeButton = event.target.closest('[data-duo-upgrade]');
      if (upgradeButton) {
        this.upgradeToDuo(upgradeButton);
        return;
      }

      const subscribeButton = event.target.closest('[data-subscribe-line]');
      if (subscribeButton) {
        this.subscribeLine(subscribeButton);
        return;
      }

      const plus = event.target.closest('[data-qty-plus]');
      if (plus) {
        this.stepQuantity(plus.closest('[data-line]'), 1);
        return;
      }

      const minus = event.target.closest('[data-qty-minus]');
      if (minus) {
        this.stepQuantity(minus.closest('[data-line]'), -1);
        return;
      }

      const remove = event.target.closest('[data-remove-line]');
      if (remove) {
        this.changeLine(Number(remove.dataset.removeLine), 0);
      }
    }

    handleChange(event) {
      const plan = event.target.closest('[data-subscribe-plan]');
      if (plan) {
        this.syncSubscribePrompt(plan);
        return;
      }

      const input = event.target.closest('[data-qty-input]');
      if (!input) return;

      const row = input.closest('[data-line]');
      if (!row) return;

      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      this.changeLine(Number(row.dataset.line), quantity);
    }

    stepQuantity(row, delta) {
      if (!row) return;
      const input = row.querySelector('[data-qty-input]');
      const step = parseInt(input.dataset.step, 10) || 1;
      const max = parseInt(input.dataset.max, 10) || Infinity;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.min(max, Math.max(0, current + delta * step));
      if (next === current) return;
      this.changeLine(Number(row.dataset.line), next);
    }

    /* ------------------------------------------------------------------ */
    /* Cart mutations                                                     */
    /* ------------------------------------------------------------------ */

    async addToCart(variantId, button) {
      if (this.busy) return;
      this.setBusy(true, button);

      try {
        const response = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Could not add to cart.');
        }

        await this.renderFromServer();
        this.notify('add', [{ merchandiseId: String(variantId), quantity: 1 }]);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setBusy(false, button);
      }
    }

    async changeLine(line, quantity) {
      if (this.busy) return;
      this.setBusy(true);

      try {
        const response = await fetch(`${ROOT}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Could not update the cart.');
        }

        await this.renderFromServer();
        this.notify(quantity > 0 ? 'update' : 'remove', [{ id: line, quantity }]);
      } catch (error) {
        this.showError(error.message);
        // Re-render anyway so displayed quantities match the real cart.
        this.renderFromServer();
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Swap one unit of a one-time line for its Duo. The Duo is added first so a
     * failed add leaves the cart as it was; then the single drops by one.
     */
    async upgradeToDuo(button) {
      if (this.busy) return;
      this.setBusy(true, button);

      const duoVariant = button.dataset.duoUpgrade;
      const lineKey = button.dataset.lineKey;
      const remaining = Math.max(0, (parseInt(button.dataset.lineQuantity, 10) || 1) - 1);

      try {
        await this.postCart('cart/add.js', { id: Number(duoVariant), quantity: 1 }, 'Could not add the Duo.');
        await this.postCart('cart/change.js', { id: lineKey, quantity: remaining }, 'Could not update the cart.');

        await this.renderFromServer();
        this.notify('update', [{ merchandiseId: String(duoVariant), quantity: 1 }]);
      } catch (error) {
        this.showError(error.message);
        this.renderFromServer();
      } finally {
        this.setBusy(false, button);
      }
    }

    /** Move a one-time line onto the selling plan picked in the prompt. */
    async subscribeLine(button) {
      if (this.busy) return;

      const prompt = button.closest('[data-subscribe-prompt]');
      const select = prompt && prompt.querySelector('[data-subscribe-plan]');
      if (!select || !select.value) return;

      this.setBusy(true, button);
      try {
        await this.postCart(
          'cart/change.js',
          {
            id: button.dataset.subscribeLine,
            quantity: parseInt(button.dataset.lineQuantity, 10) || 1,
            selling_plan: Number(select.value),
          },
          'Could not start the subscription.'
        );

        await this.renderFromServer();
        this.notify('update', [{ id: button.dataset.subscribeLine }]);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setBusy(false, button);
      }
    }

    /** Update the prompt's heading, price and saving from the selected plan. */
    syncSubscribePrompt(select) {
      const prompt = select.closest('[data-subscribe-prompt]');
      const option = select.selectedOptions[0];
      if (!prompt || !option) return;

      const set = (selector, value) => {
        const el = prompt.querySelector(selector);
        if (el && value) el.textContent = value;
      };
      set('[data-subscribe-pct]', option.dataset.pct);
      set('[data-subscribe-every]', option.dataset.every);
      set('[data-subscribe-price]', option.dataset.price);
      set('[data-subscribe-save]', option.dataset.save);
    }

    async postCart(path, body, fallbackMessage) {
      const response = await fetch(`${ROOT}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || fallbackMessage);
      }
      return response.json();
    }

    /* ------------------------------------------------------------------ */
    /* Rendering                                                          */
    /* ------------------------------------------------------------------ */

    renderFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('c-cart-drawer');
      if (!fresh) return false;

      this.innerHTML = fresh.innerHTML;
      this.bindUpsellSlider();
      return true;
    }

    async renderFromServer() {
      try {
        const response = await fetch(`${ROOT}?sections=${this.sectionId}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const sections = await response.json();
        const html = sections[this.sectionId];
        if (!html) return;

        this.renderFromHtml(html);
      } catch (error) {
        console.error('[c-cart-drawer] render failed', error);
      }
    }

    /* ------------------------------------------------------------------ */
    /* "You may also like" slider                                         */
    /* ------------------------------------------------------------------ */

    bindUpsellSlider() {
      const track = this.querySelector('[data-ymal-track]');
      if (!track) return;

      track.addEventListener('scroll', () => this.syncUpsellNav(), { passive: true });
      this.syncUpsellNav();
    }

    scrollUpsells(direction) {
      const track = this.querySelector('[data-ymal-track]');
      if (!track) return;

      const card = track.querySelector('.c-cart-ymal');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;

      track.scrollBy({ left: step * direction, behavior: 'smooth' });

      clearTimeout(this.upsellSyncTimer);
      this.upsellSyncTimer = setTimeout(() => this.syncUpsellNav(), 400);
    }

    syncUpsellNav() {
      const track = this.querySelector('[data-ymal-track]');
      const prev = this.querySelector('[data-ymal-nav="prev"]');
      const next = this.querySelector('[data-ymal-nav="next"]');
      if (!track || !prev || !next) return;

      const scrollable = track.scrollWidth > track.clientWidth + 1;
      prev.hidden = !scrollable;
      next.hidden = !scrollable;
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }

    /* ------------------------------------------------------------------ */
    /* Side effects                                                       */
    /* ------------------------------------------------------------------ */

    async notify(action, lines) {
      // Broadcast the theme's standard event so cart-icon, product forms,
      // quick-add buttons etc. stay in sync. The promise is resolved with the
      // fresh cart before dispatch since our mutation has already completed.
      try {
        const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();

        const deferred = CartLinesUpdateEvent.createPromise();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            source: SOURCE,
          },
        });

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action,
            context: 'cart',
            lines,
            promise: deferred.promise,
          })
        );
      } catch (error) {
        console.warn('[c-cart-drawer] cart sync broadcast failed', error);
      }
    }

    setBusy(state, button) {
      this.busy = state;
      this.syncBusy();
      if (button) button.disabled = state;
    }

    syncBusy() {
      this.classList.toggle('is-busy', this.busy || this.pendingExternal > 0);
    }

    showError(message) {
      const region = this.querySelector('[data-cart-error]');
      if (!region) return;
      region.textContent = message || 'Something went wrong. Please try again.';
      region.hidden = false;
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        region.hidden = true;
      }, 4000);
    }
  }

  customElements.define('c-cart-drawer', CCartDrawer);
})();
